import { z } from 'zod';
import { IdSchema, RangeSchema, withMeta } from '../common';
import {
  DashTradeSchema,
  SessionSchema as MarketSessionSchema,
  StatCardSchema,
  TickerSchema,
} from '../dataset/core';
import { DashboardDataSchema } from '../dataset/dashboard';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** dashboard.* — Dashboard (/home) and Widget Dashboard (/home/widgets): SDD §7.4 rows
 *  `dashboard.overview`, `dashboard.widgets.getLayout/saveLayout/reset`. */

/** The /home composition: the dashboard slice (equity sets, gauges, multi-account curve, sessions, hours) plus the shell
 *  figures the reference renders around it (stat cards, ticker, daily bars, market sessions, recent trades, subtitles). */
export const DashboardOverviewSchema = withMeta({
  dashboard: DashboardDataSchema,
  statCards: z.array(StatCardSchema),
  dailyBars: z.array(z.object({ amt: z.string(), h: z.number() }).strict()),
  ticker: z.array(TickerSchema),
  recentTrades: z.array(DashTradeSchema),
  marketSessions: z.array(MarketSessionSchema),
  marketSessionsOld: z.array(MarketSessionSchema),
  /** Screen subtitle copy keyed by screen id (shell chrome). */
  subtitle: z.record(z.string(), z.string()),
});
export type DashboardOverview = z.infer<typeof DashboardOverviewSchema>;

export const WidgetIdSchema = z.string().min(1).max(48);
export const WidgetPlacementSchema = z
  .object({
    widgetId: WidgetIdSchema,
    x: z.number().int().min(0).max(11),
    y: z.number().int().min(0),
    w: z.number().int().min(1).max(12),
    h: z.number().int().min(1).max(12),
  })
  .strict();
export const WidgetCatalogueItemSchema = z
  .object({
    widgetId: WidgetIdSchema,
    title: z.string(),
    /** Premium widgets render locked by entitlement (§7.4). */
    requiredTier: z.string().nullable(),
    minW: z.number().int(),
    minH: z.number().int(),
  })
  .strict();
export const WidgetLayoutSchema = withMeta({
  layoutId: IdSchema,
  version: z.number().int().nonnegative(),
  placements: z.array(WidgetPlacementSchema),
  catalogue: z.array(WidgetCatalogueItemSchema),
  locked: z.array(WidgetIdSchema),
});
export type WidgetLayout = z.infer<typeof WidgetLayoutSchema>;

export const dashboardProcedures = defineProcedures({
  'dashboard.overview.get': query(
    'Dashboard composition for a range (P&L calendar, score chart inputs, stat cards, ticker)',
    z.object({ range: RangeSchema.optional() }).strict().optional(),
    DashboardOverviewSchema,
  ),
  'dashboard.widgets.getLayout': query(
    'Widget layout + catalogue (premium widgets flagged locked)',
    z.void(),
    WidgetLayoutSchema,
  ),
  'dashboard.widgets.saveLayout': mutation(
    'Save the layout atomically (single transaction, optimistic version)',
    z
      .object({ version: z.number().int().nonnegative(), placements: z.array(WidgetPlacementSchema).max(48) })
      .strict(),
    WidgetLayoutSchema,
  ),
  'dashboard.widgets.reset': mutation('Reset the layout to the default', z.void(), WidgetLayoutSchema),
});
export type DashboardFamilyClient = RouterShape<typeof dashboardProcedures>;

export const dashboardFamily = defineFamily({
  name: 'dashboard',
  title: 'Dashboard',
  screens: ['Dashboard', 'Widget Dashboard'],
  procedures: dashboardProcedures,
  binding: 'simulated',
});
